import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaExchangeAlt,
  FaSearch,
  FaSyncAlt,
  FaUserGraduate,
  FaLock,
  FaUnlock,
  FaCheck,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectSessionClass from "../ChildComponent/SelectSessionClass";
import BackButton from "../ChildComponent/BackButton";
import MdlClassChange from "../dialog/MdlClassChange";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

//Created By Cursor
function getStaffId() {
  const staff = SessionManager.getStaffDetails();
  return staff?.staff_id || "";
}

//Created By Cursor
function isFeeCollected(student) {
  if (!student) return false;
  return (
    student.fee_collected === true ||
    student.fee_collected === 1 ||
    student.fee_collected === "1" ||
    student.admission_type_locked === true ||
    student.admission_type_locked === 1
  );
}

const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

const StudentClassChangeRow = memo(function StudentClassChangeRow({
  student,
  selected,
  index,
  onToggle,
}) {
  const locked = isFeeCollected(student);
  const classLabel =
    student.section_id === "No"
      ? student.class_name
      : `${student.class_name || ""}:${student.section_name || ""}`;

  //Created By Cursor
  const handleToggle = () => {
    if (locked) {
      myToast.notify_warning(
        "Cannot select — fee already collected or settled for this student."
      );
      return;
    }
    onToggle(student);
  };

  return (
    <div
      className={`rn-row rn-row--classchange${
        selected ? " rn-row--selected" : ""
      }${locked ? " rn-row--locked" : ""}`}
      style={{ "--rn-i": index }}
    >
      <div className="rn-row__who">
        <StudentAvatar
          name={student.stu_name}
          imagePath={student.student_img_path}
        />
        <div className="min-w-0">
          <div className="rn-row__name">{student.stu_name || "—"}</div>
          <div className="rn-row__chips">
            <span className="rn-chip rn-chip--father">
              {student.father_name ? `S/O ${student.father_name}` : "—"}
            </span>
            {classLabel ? (
              <span className="rn-chip rn-chip--class">{classLabel}</span>
            ) : null}
            {student.student_roll_no ? (
              <span className="rn-chip rn-chip--enroll">
                Roll: {student.student_roll_no}
              </span>
            ) : null}
            {locked ? (
              <span className="rn-chip rn-chip--locked">
                <FaLock aria-hidden /> Locked · Fee collected/settled
              </span>
            ) : (
              <span className="rn-chip rn-chip--unlocked">
                <FaUnlock aria-hidden /> Unlocked
              </span>
            )}
          </div>
        </div>
      </div>

      <div
        className={`rn-row__field${selected ? " rn-row__field--selected" : ""}${
          locked ? " rn-row__field--locked" : ""
        }`}
      >
        <label
          className="rn-row__label"
          htmlFor={`cc-select-${student.student_enrollment_no || index}`}
        >
          Select
        </label>
        <button
          type="button"
          id={`cc-select-${student.student_enrollment_no || index}`}
          className={`cc-select-btn${selected ? " is-selected" : ""}${
            locked ? " is-locked" : ""
          }`}
          onClick={handleToggle}
          disabled={locked}
          aria-pressed={selected}
          title={
            locked
              ? "Locked because fee is already collected or settled"
              : selected
              ? "Deselect student"
              : "Select student"
          }
        >
          {locked ? (
            <FaLock aria-hidden />
          ) : selected ? (
            <span className="cc-select-btn__tick" aria-hidden>
              <FaCheck />
            </span>
          ) : null}
          <span>
            {locked ? "Locked" : selected ? "Selected" : "Select"}
          </span>
        </button>
        {locked ? (
          <div className="rn-row__hint">
            Cannot change class — fee already collected or settled
          </div>
        ) : null}
      </div>
    </div>
  );
});

//Created By Cursor
function ClassChange() {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadClass, setLoadClass] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [boardId, setBoardId] = useState("");
  const [mediumId, setMediumId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState("-1");
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    if (mSession?.[0]) {
      setSessionId(mSession[0].session_id || "");
      setSessionName(mSession[0].session_name || "");
    }
    setLoadClass(true);
  }, []);

  //Created By Cursor
  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !sessionId) {
        setStudents([]);
        setSelectedMap({});
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = {
          school_code: schoolCode,
          class_id: classId,
          section_id: sectionId,
          session_id: sessionId,
        };

        let payload = null;
        try {
          const res = await axios.get(
            `${API_BASE()}getCurrentStudentIfFeeCollected.php`,
            { params }
          );
          payload = res?.data;
        } catch (primaryError) {
          console.warn(
            "[ClassChange] fee-collected endpoint failed, falling back",
            primaryError
          );
        }

        if (!payload || payload.api_status !== "OK") {
          try {
            const res = await axios.get(
              `${API_BASE()}getCurrentStudentForManageRecords.php`,
              { params }
            );
            payload = res?.data;
          } catch (leanError) {
            console.warn(
              "[ClassChange] lean endpoint failed, falling back",
              leanError
            );
          }
        }

        if (!payload || payload.api_status !== "OK") {
          const res = await axios.get(`${API_BASE()}getCurrentStudent.php`, {
            params,
          });
          payload = res?.data;
        }

        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          let rows = payload.data;

          const needsFeeCheck = rows.some(
            (row) =>
              row.fee_collected === undefined &&
              row.admission_type_locked === undefined
          );

          if (needsFeeCheck && rows.length > 0) {
            try {
              const ids = rows
                .map((r) => r.student_enrollment_no)
                .filter(Boolean)
                .join(",");
              const feeRes = await axios.get(
                `${API_BASE()}checkIfStudentFeeCollectedBatch.php`,
                {
                  params: {
                    student_ids: ids,
                    class_id: classId,
                    session_id: sessionId,
                    school_code: schoolCode,
                  },
                }
              );
              const feeData = feeRes?.data?.data;
              const feeMap = {};
              if (Array.isArray(feeData)) {
                feeData.forEach((item) => {
                  const id =
                    item.student_id || item.student_enrollment_no || "";
                  if (id) {
                    feeMap[id] =
                      item.fee_collected === true || item.fee_collected === 1;
                  }
                });
              } else if (feeData && typeof feeData === "object") {
                Object.keys(feeData).forEach((id) => {
                  feeMap[id] = feeData[id] === true || feeData[id] === 1;
                });
              }
              rows = rows.map((row) => {
                const collected = Boolean(feeMap[row.student_enrollment_no]);
                return {
                  ...row,
                  fee_collected: collected,
                  admission_type_locked: collected,
                };
              });
            } catch (feeError) {
              console.warn("[ClassChange] fee batch check failed", feeError);
            }
          }

          setStudents(rows);
          setSelectedMap((prev) => {
            const next = {};
            rows.forEach((row) => {
              const id = row.student_enrollment_no;
              if (!id || isFeeCollected(row)) return;
              if (prev[id]) next[id] = row;
            });
            return next;
          });
        } else {
          setStudents([]);
          setSelectedMap({});
          if (payload?.api_remark) {
            myToast.notify_warning(payload.api_remark);
          }
        }
      } catch (error) {
        console.error("[ClassChange] load error", error);
        setStudents([]);
        setSelectedMap({});
        myToast.notify_warning("Failed to load students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, sessionId]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  //Created By Cursor
  const onSessionChange = useCallback(
    (nextSessionId, nextSessionName, nextSessionStatus) => {
      setSessionId(nextSessionId || "");
      setSessionName(nextSessionName || "");
      setClassId("");
      setSectionId("");
      setBoardId("");
      setMediumId("");
      setSelectedClassPosition(0);
      setClassSectionName("");
      setSelectedMap({});
      setSearchTerm("");
      setStudents([]);
    },
    []
  );

  //Created By Cursor
  const onClassSelect = useCallback(
    (
      nextClassId,
      nextSectionId,
      nextSessionId,
      position,
      className,
      sectionName,
      nextSessionName,
      nextBoardId,
      nextMediumId
    ) => {
      setClassId(nextClassId);
      setSectionId(nextSectionId);
      setSessionId(nextSessionId);
      setSessionName(nextSessionName || "");
      setBoardId(nextBoardId || "");
      setMediumId(nextMediumId || "");
      setSelectedClassPosition(position);
      setClassSectionName(
        nextSectionId === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedMap({});
      setSearchTerm("");
    },
    []
  );

  //Created By Cursor
  const toggleStudent = useCallback((student) => {
    if (isFeeCollected(student)) return;
    const id = student.student_enrollment_no;
    if (!id) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = student;
      return next;
    });
  }, []);

  const selectedStudents = useMemo(
    () => Object.values(selectedMap),
    [selectedMap]
  );

  const selectedCount = selectedStudents.length;

  const lockedCount = useMemo(
    () => students.filter((s) => isFeeCollected(s)).length,
    [students]
  );

  const unlockedCount = students.length - lockedCount;

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_roll_no,
        s.class_name,
        s.section_name,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  //Created By Cursor
  const changeClass = useCallback(
    async (
      classIdNew,
      sectionIdNew,
      nextSessionId,
      boardIdNew,
      mediumIdNew
    ) => {
      if (!selectedStudents.length) {
        myToast.notify_error("Select Student !!!");
        return;
      }

      const staffId = getStaffId();
      const schoolCode = SessionManager.getSchoolCode();
      if (!staffId || !schoolCode) {
        myToast.notify_warning("Staff or school code is missing.");
        return;
      }

      const unlockedSelected = selectedStudents.filter(
        (s) => !isFeeCollected(s)
      );
      if (!unlockedSelected.length) {
        myToast.notify_warning(
          "All selected students are locked (fee collected)."
        );
        return;
      }

      setSubmitting(true);
      try {
        const studentList = JSON.stringify(
          unlockedSelected.map(({ student_enrollment_no, student_roll_no }) => ({
            student_enrollment_no,
            student_roll_no,
          }))
        );

        const params = {
          staff_id: staffId,
          current_class_id: classId,
          current_section_id: sectionId,
          new_class_id: classIdNew,
          new_section_id: sectionIdNew,
          current_board_id: boardId,
          current_medium_id: mediumId,
          new_board_id: boardIdNew,
          new_medium_id: mediumIdNew,
          student_list: studentList,
          session_id: sessionId,
          school_code: schoolCode,
        };

        const res = await axios.get(`${API_BASE()}updateClassChange.php`, {
          params,
        });
        const payload = res?.data;

        if (payload?.api_status === "OK") {
          myToast.notify_success(
            payload.api_remark || "Class changed successfully."
          );
          setSelectedMap({});
          await loadStudents({ soft: true });
        } else {
          myToast.notify_warning(
            payload?.api_remark || "Class change failed."
          );
        }
      } catch (error) {
        console.error("[ClassChange] update error", error);
        myToast.notify_warning("Failed to change class.");
      } finally {
        setSubmitting(false);
      }
    },
    [
      selectedStudents,
      classId,
      sectionId,
      boardId,
      mediumId,
      sessionId,
      loadStudents,
    ]
  );

  //Created By Cursor
  const handleShow = useCallback(() => {
    if (!selectedCount) {
      myToast.notify_error("Select Student !!!");
      return;
    }
    setIsOpen(true);
  }, [selectedCount]);

  //Created By Cursor
  const handleClose = useCallback(() => setIsOpen(false), []);

  //Created By Cursor
  const handleSelectedClass = useCallback(
    (...args) => {
      handleClose();
      changeClass(...args);
    },
    [changeClass, handleClose]
  );

  //Created By Cursor
  const selectAllUnlocked = useCallback(() => {
    const next = {};
    students.forEach((s) => {
      if (!isFeeCollected(s) && s.student_enrollment_no) {
        next[s.student_enrollment_no] = s;
      }
    });
    setSelectedMap(next);
  }, [students]);

  //Created By Cursor
  const clearSelection = useCallback(() => setSelectedMap({}), []);

  if (!loggedIn) return <Redirect to="/Login" />;

  const classSelected = Boolean(classId && sectionId && sessionId);
  const busy = loading || refreshing || submitting;

  const changeButton = (
    <button
      type="button"
      className="rn-btn rn-btn--solid"
      onClick={handleShow}
      disabled={busy || selectedCount === 0}
    >
      <span className="rn-btn__icon" aria-hidden>
        <FaExchangeAlt />
      </span>
      <span className="rn-btn__text">
        <span className="rn-btn__title">
          {submitting
            ? "Updating…"
            : selectedCount > 0
            ? `Change Class (${selectedCount})`
            : "Change Class"}
        </span>
        <span className="rn-btn__hint">
          {selectedCount > 0
            ? "Pick destination class"
            : "Select unlocked students"}
        </span>
      </span>
    </button>
  );

  return (
    <div
      className="content payroll-page sm-page rn-page cc-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Class Change</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaExchangeAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Class Change</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${students.length} student${
                      students.length === 1 ? "" : "s"
                    }`
                  : "Select session & class to move students"}
                {lockedCount > 0 ? ` · ${lockedCount} locked` : ""}
                {selectedCount > 0 ? ` · ${selectedCount} selected` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="rn-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="rn-content">
          {loadClass ? (
            <div className="rn-class-wrap">
              <SelectSessionClass
                action={onClassSelect}
                onSessionChange={onSessionChange}
                selected_position={selectedClassPosition}
                session_id={sessionId}
                session_name={sessionName}
                sessionChangePermission={true}
              />
            </div>
          ) : null}

          {!classSelected ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose session, class and section to load students
              </p>
            </div>
          ) : loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <div className="rn-toolbar rn-toolbar--top">
                <div className="rn-toolbar__status" aria-live="polite">
                  <span className="rn-status-chip rn-status-chip--students">
                    {students.length} student
                    {students.length === 1 ? "" : "s"}
                  </span>
                  <span className="rn-status-chip rn-status-chip--clean">
                    {unlockedCount} unlocked
                  </span>
                  {lockedCount > 0 ? (
                    <span className="rn-status-chip rn-status-chip--locked">
                      {lockedCount} locked
                    </span>
                  ) : null}
                  {selectedCount > 0 ? (
                    <span className="rn-status-chip rn-status-chip--unsaved">
                      {selectedCount} selected
                    </span>
                  ) : null}
                </div>
                <div className="cc-toolbar-actions">
                  <button
                    type="button"
                    className="rn-btn rn-btn--ghost"
                    onClick={selectAllUnlocked}
                    disabled={busy || unlockedCount === 0}
                  >
                    Select unlocked
                  </button>
                  <button
                    type="button"
                    className="rn-btn rn-btn--ghost"
                    onClick={clearSelection}
                    disabled={busy || selectedCount === 0}
                  >
                    Clear
                  </button>
                  {changeButton}
                </div>
              </div>

              {filteredStudents.length === 0 ? (
                <div className="sm-empty">
                  <p className="sm-empty__title">No students found</p>
                  <p className="sm-empty__hint">
                    {searchTerm
                      ? "Try a different search term."
                      : "This class has no active students."}
                  </p>
                </div>
              ) : (
                <div className="rn-list">
                  {filteredStudents.map((student, index) => (
                    <StudentClassChangeRow
                      key={student.id || student.student_enrollment_no}
                      student={student}
                      selected={Boolean(
                        selectedMap[student.student_enrollment_no]
                      )}
                      index={index}
                      onToggle={toggleStudent}
                    />
                  ))}
                </div>
              )}

              {students.length > 0 ? (
                <div className="rn-toolbar rn-toolbar--bottom">
                  {changeButton}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {isOpen ? (
        <MdlClassChange
          session_id={sessionId}
          session_name={sessionName}
          actionClose={handleClose}
          actionSelectClass={handleSelectedClass}
        />
      ) : null}
    </div>
  );
}

export default ClassChange;
